import { stringify } from "query-string";
import { ITournament } from "../dynamic/LatestScores";
import { errorHandling } from "./";
import queryString from "query-string";

const tournamentEndPoint = "/tennis/api/TournamentApi/GetAcceptanceList";
const calendarFiltersEndPoint = "/tennis/api/TournamentApi/GetCalendarFilters";
const latestScores = "/tennis/Api/TournamentApi/GetResultsRibbon";

export const getLatestScores = async (tournaments: ITournament[]) => {
  const response = await fetch(`${process.env.HOST}${latestScores}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(tournaments)
  });
  if (response.ok) {
    return await response.json();
  } else {
    throw Error(response.statusText);
  }
};
export interface getTournamentsProps {
  q?: string;
  skip?: number;
  take?: number | null;
  startDate?: string;
  endDate?: string;
  tourCode: string;
  doorness?: string;
  categories?: string;
  isOrderAscending?: boolean;
  orderField?: string;
  surfaceCodes?: string;
  zoneCode?: string;
  nationCodes?: string;
  drawFormat?: string;
}

/**
 * Requesting a list of tournaments from endpoint
 * @param {string} q - Search string for search tournament by name.
 * @param {number} skip - Index of the starting splice point (needed for progressive loading).
 * @param {number} take - Index of the ending splice point (needed for progressive loading).
 * @param {string} startDate - optional: starting date value.
 * @param {string} endDate - requires a start date: ending date value.
 * @param {string} tourCode - required.
 * @param {string} doorness - optional: a comma-separate string of values that represent whether a tournament was indoor/door etc. Can be an empty string.
 * @param {string} categories - optional: a comma-separate string of values that represent the tournament category/grade etc. Can be an empty string.
 * @param {boolean} isOrderAscending - required: determins whether result should ascend (true) for descend
 * @param {string} surfaceCodes - optional: a comma-separate string of values that represent the tournament surface codes etc. Can be an empty string.
 * @param {string} zoneCode - optional: zone code / nation code as provides by zone/nation code endpoint.
 * @param {string} nationCodes - optional: zone code / nation code as provides by zone/nation code endpoint.
 *
 */

export const getTournaments = ({
  q = "",
  skip = 0,
  take = 10,
  startDate = "",
  endDate = "",
  tourCode = "",
  doorness = "",
  categories = "",
  isOrderAscending = true,
  orderField = "",
  surfaceCodes = "",
  zoneCode = "",
  nationCodes = "",
  drawFormat = ""
}: getTournamentsProps) => {
  return fetch(
    `${
      process.env.HOST
    }/tennis/api/TournamentApi/GetCalendar?circuitCode=${tourCode}&searchString=${q}&skip=${skip}${
      take ? "&take=" + take : ""
    }${"&nationCodes=" +
      nationCodes +
      "&zoneCodes=" +
      zoneCode}&dateFrom=${startDate}&dateTo=${endDate}&indoorOutdoor=${doorness}&categories=${categories}&isOrderAscending=${isOrderAscending}&orderField=${orderField}&surfaceCodes=${surfaceCodes}&singlesDrawFormat=${drawFormat}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export async function getAcceptanceList({
  tournamentKey,
  circuitCode,
  playerTypeCode,
  ageCategoryCode,
  matchTypeCode
}: {
  tournamentKey: string;
  circuitCode: string;
  playerTypeCode?: string;
  ageCategoryCode?: string;
  matchTypeCode?: string;
}) {
  const response = await fetch(
    `${
      process.env.HOST
    }${tournamentEndPoint}?tournamentKey=${tournamentKey}&circuitCode=${circuitCode}${
      playerTypeCode ? "&playerTypeCode=" + playerTypeCode : ""
    }${ageCategoryCode ? "&ageCategoryCode=" + ageCategoryCode : ""}${
      matchTypeCode ? "&matchTypeCode=" + matchTypeCode : ""
    }`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    }
  );

  if (response.ok) {
    return await response.json();
  } else {
    throw Error(response.statusText);
  }
}

export async function getCalendarFilters(
  circuitCode: string,
  dateFrom: string,
  dateTo: string
) {
  const response = await fetch(
    `${process.env.HOST}${calendarFiltersEndPoint}?circuitCode=${circuitCode}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      cache: "no-cache"
    }
  );

  if (response.ok) {
    return await response.json();
  } else {
    throw Error(response.statusText);
  }
}

export const getQualificationFilters = (params: {
  tournamentKey?: number | string;
  year?: number | string;
  circuitCode?: string;
  isTeam?: string;
  isWorldChampionship?: boolean;
}) => {
  return fetch(
    `${
      process.env.HOST
    }/tennis/Api/TournamentApi/GetQualificationFilters?${queryString.stringify(
      params
    )}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getQualifications = ({
  tournamentKey,
  AgeCategories: ageCategoryCode,
  MatchTypes: matchTypeCode,
  PlayerTypes: playerTypeCode,
  circuitCode,
  year,
  isTeam,
  isWorldChampionship
}: {
  tournamentKey?: number | string;
  AgeCategories: number | string;
  MatchTypes: number | string;
  PlayerTypes: number | string;
  circuitCode?: string;
  year?: string | number;
  isTeam?: string | boolean;
  isWorldChampionship?: boolean;
}) => {
  const params = {
    tournamentKey,
    ageCategoryCode,
    matchTypeCode,
    playerTypeCode,
    circuitCode,
    year,
    isTeam,
    isWorldChampionship
  };
  return fetch(
    `${
      process.env.HOST
    }/tennis/Api/NationApi/GetNationSquad?${queryString.stringify(params)}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getEntryList = ({
  contentId,
  playerTypeCode,
  matchTypeCode
}: {
  contentId: number;
  playerTypeCode?: string | number;
  matchTypeCode?: string | number;
}) => {
  return fetch(
    `${
      process.env.HOST
    }/tennis/Api/NationApi/GetSpecialEventNationSquad?contentId=${contentId}${
      playerTypeCode ? "&playerTypeCode=" + playerTypeCode : ""
    }${matchTypeCode ? "&matchTypeCode=" + matchTypeCode : ""}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getPastChampionsFilters = ({
  tournamentGroupId: TournamentGroupId,
  isTeam = false,
  tourCode: circuitCode,
  isSeniorWorldChampionship,
  year
}: {
  tournamentGroupId?: string | number;
  isTeam?: string | boolean;
  year?: string | number;
  isSeniorWorldChampionship?: boolean;
  tourCode: string;
}) => {
  let params = {
    TournamentGroupId: TournamentGroupId,
    isTeam,
    year,
    circuitCode,
    isSeniorWorldChampionship
  };
  return fetch(
    `${
      process.env.HOST
    }/tennis/api/TournamentApi/GetPreviousChampionFilters?${stringify(params)}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getPastChampions = ({
  tournamentGroupId: TournamentGroupId,
  matchTypeCode = "",
  playerTypeCode = "",
  ageCategoryCode = "",
  nationCode = null,
  isTeam = false,
  tourCode: circuitCode = null,
  isSeniorWorldChampionship = false,
  year
}: {
  tournamentGroupId?: string | number;
  matchTypeCode?: string | number;
  playerTypeCode?: string | number;
  ageCategoryCode?: string | number;
  nationCode?: string | null;
  isTeam?: string | boolean;
  tourCode: string | null;
  isSeniorWorldChampionship?: boolean;
  year?: string | number;
}) => {
  let params = {
    TournamentGroupId: TournamentGroupId,
    isTeam,
    matchTypeCode,
    playerTypeCode,
    ageCategoryCode,
    nationCode,
    circuitCode,
    isSeniorWorldChampionship,
    year
  };
  return fetch(
    `${
      process.env.HOST
    }/tennis/api/TournamentApi/GetPreviousChampions?${stringify(params)}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getMajorEventsPreviousChampion = ({
  tournamentGroupId,
  matchTypeCode = "",
  playerTypeCode = "",
  nationCode = "",
  circuitCode = "",
  isTeam
}: {
  tournamentGroupId?: string | number;
  matchTypeCode?: string | number;
  playerTypeCode?: string | number;
  ageCategoryCode?: string | number;
  circuitCode?: string | null;
  nationCode?: string | null;
  isTeam?: boolean;
}) => {
  return fetch(
    `${
      process.env.HOST
    }/tennis/api/TournamentApi/GetPreviousChampions?TournamentGroupId=${tournamentGroupId}${
      isTeam ? "&isTeam=true" : "&isTeam=false"
    }${circuitCode ? "&circuitCode=" + circuitCode : ""}${
      matchTypeCode ? "&matchTypeCode=" + matchTypeCode : ""
    }${nationCode ? "&nationCode=" + nationCode : ""}${
      playerTypeCode ? "&playerTypeCode=" + playerTypeCode : ""
    }`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getMajorEventsPreviousChampionFilters = ({
  tournamentGroupId
}: {
  tournamentGroupId: string | number;
}) => {
  return fetch(
    `${process.env.HOST}/tennis/api/TournamentApi/GetMajorEventsPreviousChampionFilters?tournamentGroupId=${tournamentGroupId}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getMatchWinLeaderFilters = ({
  circuitCode
}: {
  tourCategory?: string;
  circuitCode: string;
}) => {
  return fetch(
    `${process.env.HOST}/tennis/Api/PlayerApi/GetMatchWinLeadersFilters?circuitCode=${circuitCode}`
  )
    .then(errorHandling)
    .then(res => res.json());
};

export const getMatchWinLeader = ({
  circuitCode,
  category,
  year,
  surfaceCode
}: {
  circuitCode: string;
  category?: string | number;
  year?: number | string;
  surfaceCode?: string | number;
}) => {
  return fetch(`${
    process.env.HOST
  }/tennis/Api/PlayerApi/GetMatchWinLeaders?circuitCode=${circuitCode}${
    category ? `&category=${category}` : ""
  }${year ? `&year=${year}` : ""}
  ${surfaceCode ? `&surfaceCode=${surfaceCode}` : ""}`)
    .then(errorHandling)
    .then(res => res.json());
};
